/**
 * MyBalanceWidget — "Where you stand" one-liner for the current user.
 *
 * The signature Splitwise feature: the first thing you see when you open
 * a trip is whether you're owed money, owe money, or are settled up.
 * We fetch from a lightweight backend endpoint so the math always matches
 * what the Settlement page would show.
 *
 * Extracted from TripOverviewPage so it can be shared with ExpensesScreen.
 */
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import clsx from 'clsx'
import { api } from '../api/client'

// Currency symbol map — kept here so both importing pages stay consistent.
export const CURRENCY_SYMBOLS = {
  USD: '$', AUD: 'A$', NZD: 'NZ$', JPY: '¥', GBP: '£', EUR: '€',
  CAD: 'C$', SGD: 'S$', HKD: 'HK$', THB: '฿', MXN: 'Mex$',
}

export default function MyBalanceWidget({ groupId }) {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['my-balance', groupId],
    queryFn: () => api.getMyBalance(groupId),
    // Cheap on the backend, safe to refetch on focus to stay current.
    staleTime: 15_000,
  })

  // If still loading, not linked to a member slot, or no data — hide entirely.
  // Without a member link the math would be meaningless.
  if (isLoading || !data || !data.linked) return null

  const sym = CURRENCY_SYMBOLS[data.currency] || data.currency + ' '
  const fmt = (n) => `${sym}${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: data.currency === 'JPY' ? 0 : 2,
    maximumFractionDigits: data.currency === 'JPY' ? 0 : 2,
  })}`

  // Three visual states: you're owed (lime green), you owe (amber), settled (muted).
  // Showing both "you paid" and "your share" below helps users trust the calculation.
  const isOwed = data.net > 0.01
  const owes = data.net < -0.01
  const accent = isOwed ? 'lime-400' : owes ? 'amber-400' : 'ink-400'
  const headline = isOwed
    ? `You're owed ${fmt(data.net)}`
    : owes
      ? `You owe ${fmt(data.net)}`
      : `You're settled up`

  return (
    <button
      onClick={() => navigate(`/groups/${groupId}/settlement`)}
      className={clsx(
        'mb-6 w-full rounded-2xl px-5 py-4 text-left animate-slide-up transition-all',
        'border bg-ink-900 hover:bg-ink-800/60',
        isOwed && 'border-lime-400/30 hover:border-lime-400/50',
        owes && 'border-amber-400/30 hover:border-amber-400/50',
        !isOwed && !owes && 'border-ink-700 hover:border-ink-600',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500 mb-1">
            Where you stand
          </div>
          <div className={`font-display text-2xl font-bold text-${accent}`}>
            {headline}
          </div>
          <div className="text-xs text-ink-500 mt-1 font-mono">
            You paid <span className="text-ink-300">{fmt(data.you_paid)}</span>
            {' · '}
            Your share <span className="text-ink-300">{fmt(data.your_share)}</span>
          </div>
        </div>
        <ChevronRight size={18} className={`text-${accent} flex-shrink-0`} />
      </div>
    </button>
  )
}
